import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Logo } from '../components';
import { ArrowRight, ArrowUpRight, ChevronUp, Calendar, Clock } from 'lucide-react';
import { getAllServicesFromStore } from '../database/dbUtils';
import { servicesSeedData } from '../database/seedData/services';
import { getAllBlogs, initializeBlogSystem } from '../database/blogStore';

// Get excerpt from blog content
const getExcerpt = (content, maxLength = 180) => {
  if (!content) return '';
  
  // Try to get executive summary first
  const summaryMatch = content.match(/## Executive Summary\n\n([^#]+)/);
  if (summaryMatch) {
    const summary = summaryMatch[1].trim();
    return summary.length <= maxLength ? summary : summary.substring(0, maxLength) + '...';
  }
  
  // Remove markdown headers and get first paragraph
  const withoutHeaders = content.replace(/^#+ .*$/gm, '').trim();
  const firstParagraph = withoutHeaders.split('\n\n')[0];
  
  if (firstParagraph.length <= maxLength) return firstParagraph;
  return firstParagraph.substring(0, maxLength) + '...';
};

// Format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Get reading time
const getReadingTime = (content) => {
  const wordsPerMinute = 200;
  const wordCount = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return Math.min(Math.max(minutes, 1), 3); // Between 1-3 minutes
};

const ServiceCard = ({ service }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const handleClick = () => {
    console.log('ServiceCard clicked:', service);

    // Validate service object and slug
    if (!service) {
      console.error('Invalid service object');
      return;
    }

    if (!service.slug) {
      console.error('Service is missing slug', service);
      return;
    }

    // Normalize slug to ensure it's URL-friendly
    const safeSlug = service.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-');

    console.log('Navigating to service:', `/services/${safeSlug}`);
    navigate(`/services/${safeSlug}`);
  };

  // Fallback if service is incomplete
  if (!service || !service.title) {
    return null;
  }

  // Professional icon mapping
  const iconMap = {
    '⬡': '◈', // Hexagon to diamond for Data Strategy
    '⬢': '◉', // Filled hexagon to concentric circle for AI Advisory
    '◬': '◊', // Angular shape to diamond outline for Generative AI
    '⬟': '◇', // Octagonal to diamond for Data Governance
    '⬠': '◍'  // Geometric shape to circled dot for Realtime Integration
  };

  const displayIcon = iconMap[service.icon] || service.icon;

  return (
    <div
      className={`p-6 rounded-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer group relative overflow-hidden ${theme === 'dark'
        ? 'bg-gray-800 border-gray-700 shadow-lg hover:shadow-xl shadow-black/10'
        : 'bg-white border-gray-100 shadow-md hover:shadow-lg'
        } border`}
      onClick={handleClick}
    >
      {/* Vertical accent bar */}
      <div className="absolute left-0 top-0 w-1.5 h-full bg-gradient-to-b from-blue-400 to-indigo-600"></div>

      <div className="text-4xl mb-5 text-center opacity-80 font-light bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-500">
        {displayIcon}
      </div>
      <h3 className={`text-xl font-semibold mb-3 text-center group-hover:text-blue-500 transition-colors ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'
        }`}>
        {service.title}
      </h3>
      <p className={`text-center mb-5 line-clamp-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
        }`}>
        {service.shortDescription || service.description || 'No description available'}
      </p>
      <div className="text-blue-400 text-center group-hover:text-blue-500 transition-colors text-sm flex items-center justify-center">
        <span>Explore Service</span>
        <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
      </div>
    </div>
  );
};

// Metrics removed - no fabricated data

const advisoryPillars = [
  {
    title: 'Board-Level Strategy',
    description: 'AI and data agendas tied to ownership, funding, and risk.',
    items: ['C-Suite Working Sessions', 'Capability Roadmaps', 'Risk Frameworks']
  },
  {
    title: 'Architect & Govern',
    description: 'Production architectures, agents, and data products with controls built in.',
    items: ['Agent Patterns', 'Data Modernization', 'MLOps & Observability']
  },
  {
    title: 'Operational Excellence',
    description: 'Measurement and operating cadence that keep AI accountable.',
    items: ['Value Realization', 'Executive KPIs', 'Hybrid Operating Models']
  }
];

const Home = () => {
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { theme } = useTheme();
  const [services, setServices] = useState(servicesSeedData);
  const [latestPost, setLatestPost] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        let result = await getAllServicesFromStore();
        // Fallback: If no services in DB, use static seed data regardless of environment
        if (result.length === 0) {
          result = servicesSeedData;
        }
        setServices(result);
      } catch {
        // Keep the prerendered layout stable by falling back without a full-page error state.
        setServices(servicesSeedData);
      }
    };
    
    const fetchLatestPost = async () => {
      try {
        await initializeBlogSystem();
        const posts = await getAllBlogs('published');
        if (posts && posts.length > 0) {
          // Sort by publishedDate and get the most recent
          const sortedPosts = posts.sort((a, b) => {
            const dateA = new Date(a.publishedDate || a.createdAt);
            const dateB = new Date(b.publishedDate || b.createdAt);
            return dateB - dateA;
          });
          setLatestPost(sortedPosts[0]);
        }
      } catch {
      }
    };
    
    fetchServices();
    fetchLatestPost();
    
    // Add scroll listener for scroll-to-top button
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={theme === 'dark' ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-700'}>
      {/* Executive Hero */}
      <section className={`relative overflow-hidden ${theme === 'dark' ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950' : 'bg-gradient-to-br from-white via-slate-50 to-blue-100/40'} py-20 lg:py-28`}>
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.4),_transparent_55%)]" />
        <div className="relative max-w-6xl mx-auto px-6 text-center lg:text-left space-y-8">
          <p className={`uppercase tracking-[0.4em] text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Shared Oxygen Advisory</p>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-950'}`}>
                AI That Answers To Enterprise Governance.
              </h1>
              <p className={`mt-6 text-xl lg:text-2xl leading-relaxed ${theme === 'dark' ? 'text-slate-200' : 'text-slate-900'}`}>
                Align AI investment to capital discipline, risk ownership, and measurable operating value.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => navigate('/contact')} 
                  className={`px-10 py-4 rounded-md text-base font-semibold transition-all ${
                    theme === 'dark' 
                      ? 'bg-white text-slate-900 hover:bg-slate-50' 
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  Schedule A Conversation
                </button>
                <button 
                  onClick={() => navigate('/services')} 
                  className={`px-10 py-4 rounded-md text-base font-semibold border transition-all ${
                    theme === 'dark' 
                      ? 'border-slate-700 text-slate-200 hover:bg-slate-900/60' 
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  Our Capabilities
                </button>
              </div>
            </div>
            <div className={`${theme === 'dark' ? 'bg-slate-900/40 border-slate-800' : 'bg-white border-slate-200 shadow-md'} rounded-2xl p-10 border`}>
              <div className="space-y-6">
                <p className="text-base leading-relaxed">
                  Strategy, governance, architecture, and delivery in one executive operating model.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Metrics section removed - no fabricated data */}

      {/* Service Spotlight */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-12">
            <div>
              <p className={`text-xs uppercase tracking-[0.4em] mb-3 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Advisory Portfolio</p>
              <h2 className={`text-3xl lg:text-4xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>How We Deliver</h2>
            </div>
              <button onClick={() => navigate('/services')} className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600">
              View Services <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => (
              <div
                key={service.slug}
                className={`rounded-[28px] border p-8 flex flex-col justify-between cursor-pointer transition-all hover:-translate-y-2 ${
                  theme === 'dark' ? 'bg-slate-900/40 border-slate-800 hover:border-slate-700' : 'bg-white border-slate-200 shadow-sm hover:shadow-2xl'
                }`}
                onClick={() => navigate(`/services/${service.slug}`)}
              >
                <div>
                  <p className={`text-sm uppercase tracking-[0.3em] mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{service.icon?.toUpperCase()}</p>
                  <h3 className={`text-2xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{service.title}</h3>
                  <p className={`${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'} leading-7 mb-6`}>{service.shortDescription}</p>
                  <ul className="space-y-3 text-sm">
                    {service.keyFeatures.slice(0, 3).map((feature) => (
                      <li key={feature} className="flex gap-2">
                        <span className="text-blue-500">—</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-8 inline-flex items-center text-sm font-semibold text-blue-500">
                  View Service <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insights - Moved up */}
      {latestPost && (
        <section className={`py-20 ${theme === 'dark' ? 'bg-slate-900' : 'bg-slate-100'}`}>
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className={`text-xs uppercase tracking-[0.4em] mb-3 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Executive Briefing</p>
                <h2 className={`text-3xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Latest Point Of View</h2>
              </div>
              <button onClick={() => navigate('/blog')} className="text-sm font-semibold text-blue-500 flex items-center gap-2">
                View Archive <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div
              onClick={() => navigate(`/blog/${latestPost.slug || latestPost.id}`)}
              className={`cursor-pointer rounded-3xl border overflow-hidden transition-all ${
                theme === 'dark' ? 'bg-slate-900/50 border-slate-800 hover:border-slate-700' : 'bg-white border-slate-200 shadow-sm hover:shadow-2xl'
              }`}
            >
              <div className="grid md:grid-cols-2">
                <div className="h-64 md:h-full overflow-hidden">
                  <img
                    src={latestPost.imageUrl || latestPost.featuredImage || '/images/blog/default.jpg'}
                    alt={latestPost.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-8 flex flex-col justify-between">
                  <div>
                    <div className={`flex flex-wrap items-center gap-4 text-xs mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(latestPost.publishedDate || latestPost.createdAt)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{getReadingTime(latestPost.content)} Min Read</span>
                    </div>
                    <h3 className={`text-2xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{latestPost.title}</h3>
                    <p className={`${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>{getExcerpt(latestPost.content)}</p>
                  </div>
                  <div className="mt-6 text-sm font-semibold text-blue-500 inline-flex items-center gap-2">
                    Read The Analysis <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Advisory Pillars */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className={`text-xs uppercase tracking-[0.4em] mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Operating System</p>
            <h2 className={`text-3xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Engagements Anchored In Three Disciplines</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {advisoryPillars.map((pillar) => (
              <div key={pillar.title} className={`${theme === 'dark' ? 'bg-slate-950/40 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'} rounded-3xl p-8`}>
                <h3 className="text-2xl font-semibold mb-4">{pillar.title}</h3>
                <p className={`mb-6 ${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>{pillar.description}</p>
                <ul className="space-y-3 text-sm">
                  {pillar.items.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="text-blue-500">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`${theme === 'dark' ? 'bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900' : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-900'} py-20`}>
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <p className="uppercase tracking-[0.4em] text-xs mb-6 text-white/60">Partner Engagement</p>
          <h2 className="text-3xl lg:text-4xl font-semibold mb-6">AI That Answers To Enterprise Governance.</h2>
              <p className="text-lg text-white/80 mb-8">Share the mandate. We’ll return a focused plan for value, architecture, governance, and delivery cadence.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/contact')} className="px-8 py-3 rounded-full bg-white text-slate-900 font-semibold">Book Executive Session</button>
            <button onClick={() => navigate('/services')} className="px-8 py-3 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10">Review Services</button>
          </div>
        </div>
      </section>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 p-3 rounded-full shadow-lg border z-50 ${theme === 'dark' ? 'bg-gray-800/80 text-blue-400 border-gray-700 hover:bg-gray-700' : 'bg-white/80 text-blue-600 border-gray-100 hover:bg-blue-50'}`}
          aria-label="Scroll To Top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default Home;
